/**
 * Utility functions for balance detection and payload building.
 * Used across contest entry flows (EnterContestModal, QuickPicks) to handle
 * combined cash + credits balance scenarios.
 */

/**
 * Detects if a combined balance (both cash AND credits) was used for an entry.
 * This occurs when both balances changed after an entry submission while
 * the user had "Use Credits" enabled.
 *
 * @param cashBefore - Cash balance before entry submission
 * @param cashAfter - Cash balance after entry submission
 * @param creditsBefore - Credits balance before entry submission
 * @param creditsAfter - Credits balance after entry submission
 * @param payWithCredits - Whether the user had "Use Credits" enabled
 * @returns true if both balances changed while payWithCredits was enabled
 */
export function detectCombinedBalanceUsage(
  cashBefore: number,
  cashAfter: number,
  creditsBefore: number,
  creditsAfter: number,
  payWithCredits: boolean
): boolean {
  const cashChanged = cashBefore !== cashAfter;
  const creditsChanged = creditsBefore !== creditsAfter;
  return cashChanged && creditsChanged && payWithCredits;
}

/**
 * Payload for dual balance display (when both cash AND credits changed).
 * Used for combined balance entries.
 */
export type DualBalancePayload = {
  cashBalance: number;
  previousCashBalance: number;
  creditsBalance: number;
  previousCreditsBalance: number;
};

/**
 * Payload for single balance display (when only cash OR credits changed).
 * Used for legacy single-source entries.
 */
export type SingleBalancePayload = {
  usedCredits: boolean;
  previousBalance: number;
  newBalance: number;
};

/**
 * Builds the appropriate balance payload for success modals based on whether
 * a combined balance was used. Returns dual balance props when both changed,
 * or single balance props for legacy scenarios.
 *
 * @param usedCombinedBalance - Whether combined balance was used (from detectCombinedBalanceUsage)
 * @param cashBefore - Cash balance before entry
 * @param cashAfter - Cash balance after entry
 * @param creditsBefore - Credits balance before entry
 * @param creditsAfter - Credits balance after entry
 * @param payWithCredits - Whether user paid with credits
 * @returns Payload object for BalanceUpdate component (dual or single)
 */
export function buildBalanceUpdatePayload(
  usedCombinedBalance: boolean,
  cashBefore: number,
  cashAfter: number,
  creditsBefore: number,
  creditsAfter: number,
  payWithCredits: boolean
): DualBalancePayload | SingleBalancePayload {
  if (usedCombinedBalance) {
    return {
      cashBalance: cashAfter,
      previousCashBalance: cashBefore,
      creditsBalance: creditsAfter,
      previousCreditsBalance: creditsBefore,
    };
  }
  return {
    usedCredits: payWithCredits,
    previousBalance: payWithCredits ? creditsBefore : cashBefore,
    newBalance: payWithCredits ? creditsAfter : cashAfter,
  };
}

/**
 * Type guard to check if payload is dual balance format.
 */
export function isDualBalancePayload(
  payload: DualBalancePayload | SingleBalancePayload
): payload is DualBalancePayload {
  return 'cashBalance' in payload;
}

/**
 * Props for dual balance display components.
 */
export type DualBalanceDisplayProps = {
  cashBalance: number;
  previousCashBalance: number;
  creditsBalance: number;
  previousCreditsBalance: number;
};

/**
 * Props for single balance display components.
 */
export type SingleBalanceDisplayProps = {
  balance: number;
  previousBalance: number;
  usedCredits: boolean;
};

/**
 * Determines if dual balance props are valid for display.
 * All four values must be defined numbers and at least one balance must have changed.
 */
export function hasDualBalanceProps(props: {
  cashBalance?: number;
  previousCashBalance?: number;
  creditsBalance?: number;
  previousCreditsBalance?: number;
}): props is DualBalanceDisplayProps {
  const hasAllValues =
    typeof props.previousCashBalance === 'number' &&
    typeof props.cashBalance === 'number' &&
    typeof props.previousCreditsBalance === 'number' &&
    typeof props.creditsBalance === 'number';

  if (!hasAllValues) {
    return false;
  }

  // Only show if at least one balance actually changed
  const cashChanged = props.previousCashBalance !== props.cashBalance;
  const creditsChanged = props.previousCreditsBalance !== props.creditsBalance;

  return cashChanged || creditsChanged;
}

/**
 * Determines if single balance props are valid for display.
 * Both balance values must be defined numbers and the balance must have changed.
 */
export function hasSingleBalanceProps(props: {
  balance?: number;
  previousBalance?: number;
}): props is { balance: number; previousBalance: number } {
  const hasAllValues =
    typeof props.previousBalance === 'number' && typeof props.balance === 'number';

  if (!hasAllValues) {
    return false;
  }

  // Only show if balance actually changed
  return props.previousBalance !== props.balance;
}

